import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import Modal from './Modal';
import { createSite } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { SITE_COUNTRIES, DEFAULT_SITE_COUNTRY } from '../constants/locations';

const AddSite = ({ onSiteAdded, initialSetup = false, customerId, noun = 'site', trigger = 'icon' }) => {
        const { user } = useContext(AuthContext);
        const [siteName, setSiteName] = useState('');
        const [country, setCountry] = useState(DEFAULT_SITE_COUNTRY);
        const [error, setError] = useState(null);
        const [isAdding, setIsAdding] = useState(false);
        const { showToast } = useToast();

        // Use provided customerId or fall back to user's customerId
        // Ensure it's parsed as integer (URL params come as strings)
        const targetCustomerId = parseInt(customerId || user?.customerId);

        // Customers with a single site never see the word "site" — to them it is
        // their location — so the noun is caller-supplied and drives every string.
        const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);

        // 'icon' suits the dashboard toolbar, where it sits among other icon
        // buttons. 'button' is the hero action of the initial-setup empty state.
        // 'subtle' is for the settings Location card, where adding a second
        // location is a rare action that shouldn't outshout the card's own Save.
        const triggerStyle = initialSetup ? 'button' : trigger;
        const TRIGGER_CLASSES = {
                icon: 'p-2 text-gray-400 hover:text-green-600 transition-colors',
                button: 'inline-flex items-center gap-2 px-4 py-2 bg-brand text-white font-medium rounded-xl hover:bg-brand/90 transition-colors',
                subtle: 'inline-flex items-center gap-2 px-3 py-1 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors',
        };

        const handleSubmit = async (e) => {
                e.preventDefault();
                setError(null);

                if (!siteName.trim()) {
                        setError(`${Noun} name is required`);
                        return;
                }

                try {
                        const response = await createSite({
                                name: siteName,
                                country: country,
                                customer_id: targetCustomerId
                        });

                        if (!response.ok) {
                                throw new Error(`Failed to create ${noun}`);
                        }

                        const newSite = await response.json();
                        onSiteAdded(newSite.data);
                        setSiteName('');
                        setCountry(DEFAULT_SITE_COUNTRY);
                        setIsAdding(false);
                        showToast('success', `${Noun} created successfully`);
                } catch (err) {
                        setError(err.message);
                        showToast('error', `Failed to create ${noun}`);
                }
        };

        return (
                <>
                        <button
                                onClick={() => setIsAdding(true)}
                                data-testid="add-site-open"
                                className={TRIGGER_CLASSES[triggerStyle]}
                                title={triggerStyle === 'icon' ? `Add ${noun}` : undefined}
                        >
                                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                                {triggerStyle !== 'icon' && <span>Add {noun}</span>}
                        </button>
                        <Modal isOpen={isAdding} onClose={() => setIsAdding(false)}>
                                <form onSubmit={handleSubmit}>
                                        <h3 className="text-lg font-bold mb-4">Add a new {noun}</h3>
                                        <div className="space-y-3">
                                                <input
                                                        type="text"
                                                        value={siteName}
                                                        onChange={(e) => setSiteName(e.target.value)}
                                                        placeholder={`Enter ${noun} name`}
                                                        data-testid="add-site-name"
                                                        className="w-full p-2 border rounded-md"
                                                />
                                                <div>
                                                        <label className="block text-sm text-gray-600 mb-1">Country</label>
                                                        <select
                                                                data-testid="add-site-country"
                                                                value={country}
                                                                onChange={(e) => setCountry(e.target.value)}
                                                                className="w-full p-2 border rounded-md"
                                                        >
                                                                {SITE_COUNTRIES.map((c) => (
                                                                        <option key={c} value={c}>{c}</option>
                                                                ))}
                                                        </select>
                                                </div>
                                                <button type="submit" data-testid="add-site-submit" className="w-full px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/90">
                                                        Create {noun}
                                                </button>
                                        </div>
                                        {error && <p data-testid="add-site-error" className="text-red-500 mt-2">{error}</p>}
                                </form>
                        </Modal>
                </>
        );
};

export default AddSite;
